import { useContext, useEffect, useState } from 'react';
import { Box, Button, Stack, SvgIcon, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Plus } from '../../assets';
import { UserContext } from '../../components/shared/useUser';
import { useNavigate, useLocation } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import CataloguesTab from './components/CataloguesTab';
import { client } from '../../app/services/api/orderManagementClient';
import useLogError from '../../hooks/useLogError';

const CataloguesPage = () => {
  const { t } = useTranslation('catalogues');
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logError } = useLogError();

  const { hasPermissionTo, selectedStore } = useContext(UserContext);
  const [hasPermissionToCreateCatalogue, setHasPermissionToCreateCatalogue] = useState<boolean>(false);

  const [currentFilterInstance, setCurrentFilterInstance] = useState<string>('');
  const [currentFilterMarket, setCurrentFilterMarket] = useState<string>('');
  const [currentFilterSearchTerm, setCurrentFilterSearchTerm] = useState<string>('');
  const [isInitializingFilters, setIsInitializingFilters] = useState<boolean>(false);

  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  useEffect(() => {
    setHasPermissionToCreateCatalogue(hasPermissionTo(['CatalogueCreate', 'ConfigurationAdmin']));

    const searchParams = new URLSearchParams(location.search);
    const companyIdParam = searchParams.get('companyId');
    const customerTypeIdParam = searchParams.get('customerTypeId');
    const searchTermParam = searchParams.get('searchTerm');

    setCurrentFilterInstance(companyIdParam || '');
    setCurrentFilterMarket(customerTypeIdParam || '');
    setCurrentFilterSearchTerm(searchTermParam || '');
  }, [hasPermissionTo, location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.get('companyId') && !searchParams.get('customerTypeId')) {
      loadStoreDefaultFilters();
    }
  }, [selectedStore]);

  const handleCreateNew = () => {
    navigate('/catalogues/create');
  };

  const handleFilterChange = (instance: string, market: string, searchTerm?: string) => {
    setCurrentFilterInstance(instance);
    setCurrentFilterMarket(market);
    const newSearchTerm = searchTerm !== undefined ? searchTerm : currentFilterSearchTerm;
    setCurrentFilterSearchTerm(newSearchTerm);

    const params = new URLSearchParams();
    if (instance) params.set('companyId', instance);
    if (market) params.set('customerTypeId', market);
    if (newSearchTerm) params.set('searchTerm', newSearchTerm);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const canViewCatalogues = hasPermissionTo(['CatalogueView', 'ConfigurationAdmin']);

  if (!canViewCatalogues) {
    return null;
  }

  const loadStoreDefaultFilters = async () => {
    if (!selectedStore?.storeNumber || !selectedStore?.franchiseUUID) return;
    try {
      const companyResult = await client.getCompanyIdByStoreNumber(selectedStore.storeNumber);
      if (!companyResult.success || !companyResult.data) return;

      const instanceId = companyResult.data.toString();
      const marketsResponse = await client.getMarkets({
        companyId: instanceId,
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (marketsResponse.success && marketsResponse.data?.items) {
        const storeMarket = marketsResponse.data.items.find(m => m.uuid === selectedStore!.franchiseUUID);
        if (storeMarket) {
          const newInstance = storeMarket.companyId.toString();
          const newMarket = storeMarket.customerTypeId.toString();
          setCurrentFilterInstance(newInstance);
          setCurrentFilterMarket(newMarket);
          const params = new URLSearchParams();
          params.set('companyId', newInstance);
          params.set('customerTypeId', newMarket);
          navigate(`?${params.toString()}`, { replace: true });
        }
      }
    } catch (error) {
      logError('Failed to load store default filters');
    }
  };

  return (
    <PageArea>
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
          mb: 4, // Add margin bottom for spacing
        }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle>{t('management.pageTitle')}</PageTitle>
        </Box>

        {hasPermissionToCreateCatalogue && (
          <Box
            width={isMobile ? '100%' : 'auto'}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <Button
              variant="primary"
              size="lg"
              startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}
              onClick={handleCreateNew}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {t('management.createNew')}
            </Button>
          </Box>
        )}
      </Stack>

      <CataloguesTab 
        onCreateNew={handleCreateNew}
        onFilterChange={handleFilterChange}
        initialInstance={currentFilterInstance}
        initialMarket={currentFilterMarket}
        initialSearchTerm={currentFilterSearchTerm} />
    </PageArea>
  );
};

export default CataloguesPage;
